import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobBoardApi } from '../../services/api';
import {
  Spinner,
  Modal,
  EmptyState,
} from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  BriefcaseIcon,
  MapPinIcon,
  CalendarIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';

const STATUS = {
  draft:  'bg-gray-100 text-gray-700 border-gray-200',
  open:   'bg-green-100 text-green-800 border-green-200',
  closed: 'bg-red-100 text-red-800 border-red-200',
  filled: 'bg-blue-100 text-blue-800 border-blue-200',
};

export default function PostingDetail() {
  const { id } = useParams();
  const qc = useQueryClient();

  const [open, setOpen]   = useState(false);
  const [cover, setCover] = useState('');

  const { data: posting, isLoading, error } = useQuery({
    queryKey: ['job-posting', id],
    queryFn:  () => jobBoardApi.get(id).then(r => r.data),
    enabled:  !!id,
  });

  const { data: matches = [] } = useQuery({
    queryKey: ['my-job-matches'],
    queryFn:  () => jobBoardApi.myMatches().then(r => r.data),
  });

  const match = matches.find(m => m.id === Number(id));

  const applyMut = useMutation({
    mutationFn: data => jobBoardApi.apply(id, data),
    onSuccess:  () => {
      toast.success('Application submitted');
      qc.invalidateQueries({ queryKey: ['my-job-matches'] });
      qc.invalidateQueries({ queryKey: ['job-posting', id] });
      setOpen(false);
      setCover('');
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to apply'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (error || !posting) return (
    <div className="p-6 max-w-4xl mx-auto">
      <EmptyState title="Posting not found" description="This job posting may have been removed." />
    </div>
  );

  const applied = match?.application_status;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Link to="/app/job-board" className="text-sm text-blue-600 hover:underline mb-4 inline-block">
        ← Back to Job Board
      </Link>

      <div className="card p-6 mb-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-lg flex items-center justify-center shrink-0">
            <BriefcaseIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold text-gray-900">{posting.title}</h1>
              <span className={clsx(
                'inline-flex px-2 py-0.5 rounded-full text-xs font-medium border capitalize',
                STATUS[posting.status] || STATUS.draft
              )}>
                {posting.status}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <BuildingOffice2Icon className="w-3.5 h-3.5" />
                {posting.department?.name || 'No department'}
              </span>
              {posting.location && (
                <span className="inline-flex items-center gap-1">
                  <MapPinIcon className="w-3.5 h-3.5" />
                  {posting.location}{posting.is_remote ? ' · Remote OK' : ''}
                </span>
              )}
              {posting.closes_at && (
                <span className="inline-flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  Closes {String(posting.closes_at).slice(0, 10)}
                </span>
              )}
            </div>
          </div>
          {match && (
            <div className="text-right shrink-0">
              <p className="text-3xl font-bold text-blue-600">{match.match_pct}%</p>
              <p className="text-[10px] text-gray-400 uppercase">your match</p>
            </div>
          )}
        </div>

        {(posting.required_role || posting.required_band) && (
          <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 mb-4">
            <span className="font-medium">Target:</span>{' '}
            <span className="capitalize">{posting.required_role?.replace('_', ' ') || '—'}</span> ·{' '}
            <span className="capitalize">{posting.required_band || '—'}</span>
          </div>
        )}

        {posting.description && (
          <div className="mb-4">
            <h2 className="section-title mb-2">About this role</h2>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{posting.description}</p>
          </div>
        )}

        {posting.required_skills?.length > 0 && (
          <div className="mb-4">
            <h2 className="section-title mb-2">Required skills</h2>
            <div className="flex flex-wrap gap-1.5">
              {posting.required_skills.map(s => {
                const matched = match?.matched_skills?.includes(s.toLowerCase());
                return (
                  <span key={s} className={clsx(
                    'px-2 py-0.5 text-xs rounded-full border capitalize',
                    matched
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-gray-50 text-gray-700 border-gray-200'
                  )}>
                    {s}{matched ? ' ✓' : ''}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {posting.preferred_skills?.length > 0 && (
          <div className="mb-4">
            <h2 className="section-title mb-2">Preferred skills</h2>
            <div className="flex flex-wrap gap-1.5">
              {posting.preferred_skills.map(s => {
                const matched = match?.matched_skills?.includes(s.toLowerCase());
                return (
                  <span key={s} className={clsx(
                    'px-2 py-0.5 text-xs rounded-full border capitalize',
                    matched
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-indigo-50 text-indigo-700 border-indigo-100'
                  )}>
                    {s}{matched ? ' ✓' : ''}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {posting.applications_count ?? 0} applicants so far
          </p>
          {posting.status !== 'open' ? (
            <span className="text-sm text-gray-500">Not accepting applications</span>
          ) : applied ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border bg-blue-50 text-blue-800 border-blue-200 capitalize">
              Application {applied.replace('_', ' ')}
            </span>
          ) : (
            <button className="btn-primary" onClick={() => setOpen(true)}>Apply now</button>
          )}
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={`Apply · ${posting.title}`} size="md">
        <div className="space-y-4">
          <div>
            <label className="label">Cover letter (optional)</label>
            <textarea
              className="input h-32 resize-none"
              value={cover}
              onChange={e => setCover(e.target.value)}
              placeholder="Why are you a good fit? What would you bring to the role?"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
            <button
              disabled={applyMut.isPending}
              onClick={() => applyMut.mutate({ cover_letter: cover })}
              className="btn-primary disabled:opacity-40"
            >
              {applyMut.isPending ? 'Submitting…' : 'Submit application'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
